import { useQuery } from "@tanstack/react-query";
import { getUsage } from "@/api/endpoints";
import { Badge, Card, PageTitle } from "@/components/ui/primitives";
import { DataState } from "@/components/data-state";
import type { UsageRow } from "@/types";

/** Cents matter here — a month of scanning costs less than a dollar. */
const money = (usd: number) =>
  usd >= 1 ? `$${usd.toFixed(2)}` : `${(usd * 100).toFixed(2)}¢`;

function bytes(value: number) {
  if (value <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function planTone(row: UsageRow) {
  if (row.plan === "premium") return "success" as const;
  if (row.plan === "grace") return "warning" as const;
  if (row.plan === "free") return "neutral" as const;
  return "danger" as const;
}

function subscriptionLabel(row: UsageRow) {
  if (!row.platform) return "—";
  const store = { apple: "App Store", google: "Play", manual: "Elle verildi" }[row.platform];
  return row.subscriptionState ? `${store} · ${row.subscriptionState}` : store;
}

export function UsagePage() {
  const usage = useQuery({ queryKey: ["system-usage"], queryFn: getUsage });
  const totals = usage.data?.totals;

  return (
    <div className="space-y-4">
      <PageTitle>Kullanım ve maliyet</PageTitle>

      {totals && (
        <div className="grid gap-3 sm:grid-cols-4">
          <Summary label="Bu ay AI" value={money(totals.aiCostMonth)} note={`${totals.aiCallsMonth} çağrı`} />
          <Summary label="Toplam AI" value={money(totals.aiCostTotal)} note={`${totals.aiCallsTotal} çağrı`} />
          <Summary label="Depolama" value={bytes(totals.storageBytes)} note={`${totals.files} dosya`} />
          <Summary
            label="Organizasyon"
            value={String(usage.data?.items.length ?? 0)}
            note="en pahalı üstte"
          />
        </div>
      )}

      <DataState
        isLoading={usage.isLoading}
        isError={usage.isError}
        isEmpty={(usage.data?.items.length ?? 0) === 0}
        onRetry={() => void usage.refetch()}
      >
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-3 font-medium">Organizasyon</th>
                <th className="p-3 font-medium">Plan</th>
                <th className="p-3 font-medium">Abonelik</th>
                <th className="p-3 text-right font-medium">Kişi</th>
                <th className="p-3 text-right font-medium">Bu ay AI</th>
                <th className="p-3 text-right font-medium">Toplam AI</th>
                <th className="p-3 text-right font-medium">Depolama</th>
                <th className="p-3 font-medium">Son tarama</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {usage.data?.items.map((row) => (
                <tr key={row.orgId} className="hover:bg-muted/50">
                  <td className="p-3">
                    <p className="font-medium">{row.name}</p>
                    <p className="text-xs text-muted-foreground">{row.ownerEmail ?? "—"}</p>
                  </td>
                  <td className="p-3">
                    <Badge tone={planTone(row)}>{row.plan}</Badge>
                    {row.planTier && (
                      <span className="ml-1 text-xs text-muted-foreground">{row.planTier}</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{subscriptionLabel(row)}</td>
                  <td className="p-3 text-right tabular-nums">{row.members}</td>
                  <td className="p-3 text-right tabular-nums">
                    {row.aiCallsMonth === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <>
                        <span className="font-medium">{money(row.aiCostMonth)}</span>
                        <span className="ml-1 text-xs text-muted-foreground">
                          {row.aiCallsMonth}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="p-3 text-right tabular-nums text-muted-foreground">
                    {row.aiCallsTotal === 0 ? "—" : `${money(row.aiCostTotal)} · ${row.aiCallsTotal}`}
                  </td>
                  <td className="p-3 text-right tabular-nums">
                    {bytes(row.storageBytes)}
                    {row.files > 0 && (
                      <span className="ml-1 text-xs text-muted-foreground">{row.files}</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {row.lastAiAt ? new Date(row.lastAiAt).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </DataState>
    </div>
  );
}

function Summary({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <Card className="p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground">{note}</p>
    </Card>
  );
}
